import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { ISegment } from 'models/response/InspectionResponse';
import {
  useLazyFetchSegmentsQuery,
  usePatchFinishInspectionMutation,
} from 'services/modules/inspection';
import { Button, Footer, Header, LoadingLottie } from 'ui';
import { Modal } from 'ui/Modal';

import { routes } from 'utils/routeConfig';

import Card from './Components/Card';
import {
  ButtonIcon,
  CarDetailsBox,
  CarID,
  CarPicturesBox,
  CarPicturesLabel,
  Container,
  Content,
  ErrorBodyWrapper,
  ErrorButtons,
  ItemsWrapper,
  ProgressPercent,
  Status,
  StatusBar,
} from './CarSegmentsElements';

export const CarSegments: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { currentCar } = useAppSelector((state) => state.inspection);
  const [parts, setParts] = useState<ISegment[]>([]);
  const [fetchSegments, { data, isLoading, isSuccess }] = useLazyFetchSegmentsQuery();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const toggleShowConfirmModal = () => setShowConfirmModal((state) => !state);

  useEffect(() => {
    if (params.slug) {
      fetchSegments(params.slug);
    }
  }, []);

  useEffect(() => {
    if (isSuccess && data) {
      setParts(data.filter((v) => v.slug.includes('inspection')));
    }
  }, [isSuccess, data]);

  const routeToPictures = () => {
    if (params.slug) {
      navigate(routes.carPictures.replace(':slug', params.slug));
    }
  };
  const routeToDocuments = () => {
    if (params.slug) {
      navigate(
        routes.documents.replace(':slug', params.slug).replace(':segment_slug', 'documents'),
      );
    }
  };

  const routeToCarDetail = () => {
    if (params.slug) {
      navigate(routes.carDetails.replace(':slug', params.slug));
    }
  };

  const routeToSegmentDetail = (segment_slug: string, name: string) => {
    if (params.slug) {
      navigate(
        routes.segmentDetail
          .replace(':inspection_slug', params.slug)
          .replace(':segment_slug', segment_slug)
          .replace(':title', name.replace('/', '-')),
      );
    }
  };

  const [
    finishInspection,
    {
      isLoading: isLoadingFinishInspection,
      isSuccess: isSuccessFinishInspection,
      error: errorFinishInspection,
      isError: isErrorFinishInspection,
    },
  ] = usePatchFinishInspectionMutation();

  const completeInspection = () => {
    const { slug } = params;
    if (slug) {
      finishInspection(slug);
    }
  };

  useEffect(() => {
    if (isSuccessFinishInspection) {
      toggleShowConfirmModal();
      navigate(-1);
    }
    if (isErrorFinishInspection && errorFinishInspection) {
      toggleShowConfirmModal();
    }
  }, [isSuccessFinishInspection, isErrorFinishInspection, errorFinishInspection]);

  return (
    <Container>
      <Header
        title={
          currentCar
            ? `${currentCar?.model_full_name.make} ${currentCar?.model_full_name.model} ${currentCar?.model_full_name.year}`
            : ''
        }
        hasBack={true}
      />
      <Content>
        <StatusBar>
          <CarID>{`${t('inspection.car_number')}: ${currentCar?.car_number}`}</CarID>
          <Status status={currentCar?.status}>{currentCar?.status_text}</Status>
        </StatusBar>
        <CarDetailsBox onClick={routeToCarDetail}>
          <span className="icon-Group-33827">
            <span className="path1"></span>
            <span className="path2"></span>
            <span className="path3"></span>
            <span className="path4"></span>
            <span className="path5"></span>
            <span className="path6"></span>
            <span className="path7"></span>
            <span className="path8"></span>
            <span className="path9"></span>
            <span className="path10"></span>
            <span className="path11"></span>
          </span>
          <CarPicturesLabel>{t('car_inspection.car_information')}</CarPicturesLabel>
          <span
            className={'icon-Big-Arrows tw-text-lg -tw-rotate-90 tw-absolute tw-right-[32px]'}
          />
        </CarDetailsBox>
        <CarPicturesBox onClick={routeToPictures}>
          {currentCar && (
            <ProgressPercent>{`${currentCar.templates_data.percent.toFixed(0)}٪`}</ProgressPercent>
          )}
          <span className="icon-camera">
            <span className="path1" />
            <span className="path2" />
            <span className="path3" />
          </span>
          <CarPicturesLabel>{t('car_inspection.pictures_of_car')}</CarPicturesLabel>
        </CarPicturesBox>
        {isLoading ? (
          <LoadingLottie />
        ) : (
          <ItemsWrapper>
            {parts
              .sort((a, b) => a.order - b.order)
              .map((item, index) => (
                <Card
                  key={item.slug}
                  item={item}
                  routeToDocuments={() => routeToDocuments()}
                  routeToSegmentDetail={() => routeToSegmentDetail(item.slug, item.name)}
                />
              ))}
          </ItemsWrapper>
        )}
      </Content>
      <Footer isForceGetDetail={true} slug={params.slug}>
        <Button
          label={t('footer.complete_inspection')}
          size={'large'}
          onClick={toggleShowConfirmModal}
        >
          <ButtonIcon className={'icon-Icons'} />
        </Button>
      </Footer>
      <Modal
        title={t('car_inspection.modal_title')}
        onCloseModal={toggleShowConfirmModal}
        isShow={showConfirmModal}
      >
        <ErrorBodyWrapper>
          <p>{t('car_inspection.modal_content')}</p>
        </ErrorBodyWrapper>
        <ErrorButtons>
          <Button fullWidth onClick={completeInspection} isLoading={isLoadingFinishInspection}>
            {t('car_inspection.confirm')}
          </Button>
          <Button fullWidth color="red" onClick={toggleShowConfirmModal}>
            {t('car_inspection.cancel')}
          </Button>
        </ErrorButtons>
      </Modal>
    </Container>
  );
};
